import { useMemo, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDataFromAPI } from "app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/api/dataGridApiService";
import type {
  FilterTrayCategory,
  FilterTrayOption,
  FilterTraySelection,
} from "app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ";

const USERS_ENDPOINT = "api/v2/users/datagrid";
const FILTER_TRAY_PAGE_SIZE = 500;
const USERS_OPTION_FILTER_KEYS = ["roles", "dealer_group_id", "status"] as const;

const USERS_CATEGORY_LABELS: Record<string, string> = {
  roles: "Role",
  dealer_group_id: "Dealer Group",
  status: "Account Status",
};

function normalizeValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(v => String(v ?? "").trim())
      .filter(Boolean);
  }

  const text = String(value ?? "").trim();
  return text ? [text] : [];
}

function deriveValueMap(rows: any[]): Record<string, string[]> {
  const valueSetByKey: Record<string, Set<string>> = {};

  for (const row of rows) {
    for (const key of USERS_OPTION_FILTER_KEYS) {
      if (!valueSetByKey[key]) valueSetByKey[key] = new Set<string>();
      const values = normalizeValue(row?.[key]);
      values.forEach(v => valueSetByKey[key].add(v));
    }
  }

  const valueMap: Record<string, string[]> = {};
  for (const key of USERS_OPTION_FILTER_KEYS) {
    valueMap[key] = Array.from(valueSetByKey[key] ?? []).sort((a, b) =>
      a.localeCompare(b),
    );
  }

  return valueMap;
}

export interface UseUsersFilterTrayResult {
  categories: FilterTrayCategory[];
  optionsForActiveCategory: FilterTrayOption[];
  selectedByCategory: FilterTraySelection;
  onSelectionChange: (categoryId: string, selectedValues: string[]) => void;
  onClearAllFilters: () => void;
  onClearActiveCategory: () => void;
  onClearAllInPanel: () => void;
  quickFilters: Record<string, string[]>;
  totalActiveCount: number;
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string | null) => void;
  isLoading: boolean;
  error: Error | null;
}

export function useUsersFilterTray(): UseUsersFilterTrayResult {
  const [selectedByCategory, setSelectedByCategory] = useState<FilterTraySelection>(
    {},
  );
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const quickFilters = useMemo(() => {
    const output: Record<string, string[]> = {};
    for (const [key, values] of Object.entries(selectedByCategory)) {
      if (Array.isArray(values) && values.length > 0) {
        output[key] = values;
      }
    }
    return output;
  }, [selectedByCategory]);

  const params = useMemo(
    () => ({
      "audit_meta.created_user_type": "USER",
      limit: FILTER_TRAY_PAGE_SIZE,
      ...Object.fromEntries(
        Object.entries(quickFilters).map(([key, values]) => [key, values.join(",")]),
      ),
    }),
    [quickFilters],
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["users-filter-tray", params],
    queryFn: () => fetchDataFromAPI(USERS_ENDPOINT, params, 1, FILTER_TRAY_PAGE_SIZE),
  });

  const valueMap = useMemo(() => {
    const rows = data?.body?.data ?? [];
    return deriveValueMap(rows);
  }, [data]);

  const categories: FilterTrayCategory[] = useMemo(
    () =>
      USERS_OPTION_FILTER_KEYS.map(key => ({
        id: key,
        label: USERS_CATEGORY_LABELS[key] ?? key,
        selectedCount: (selectedByCategory[key] ?? []).length,
      })),
    [selectedByCategory],
  );

  const optionsForActiveCategory: FilterTrayOption[] = useMemo(() => {
    if (!activeCategoryId) return [];
    const optionsFromData = valueMap[activeCategoryId] ?? [];
    const selectedOptions = selectedByCategory[activeCategoryId] ?? [];
    const combined = Array.from(
      new Set([...optionsFromData, ...selectedOptions]),
    ).sort((a, b) => a.localeCompare(b));
    return combined.map(value => ({ value, label: value }));
  }, [activeCategoryId, valueMap, selectedByCategory]);

  const onSelectionChange = useCallback(
    (categoryId: string, selectedValues: string[]) => {
      setSelectedByCategory(previous => ({
        ...previous,
        [categoryId]: selectedValues,
      }));
    },
    [],
  );

  const onClearAllFilters = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const onClearActiveCategory = useCallback(() => {
    if (!activeCategoryId) return;
    setSelectedByCategory(previous => ({
      ...previous,
      [activeCategoryId]: [],
    }));
  }, [activeCategoryId]);

  const onClearAllInPanel = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const totalActiveCount = useMemo(
    () =>
      Object.values(selectedByCategory).reduce(
        (total, selectedValues) => total + (selectedValues?.length ?? 0),
        0,
      ),
    [selectedByCategory],
  );

  return {
    categories,
    optionsForActiveCategory,
    selectedByCategory,
    onSelectionChange,
    onClearAllFilters,
    onClearActiveCategory,
    onClearAllInPanel,
    quickFilters,
    totalActiveCount,
    activeCategoryId,
    setActiveCategoryId,
    isLoading: !!isLoading || !!isFetching,
    error: error as Error | null,
  };
}
